(function(root) {
  'use strict';

  var EXPECTED_ANCHORS = [
    [25, 1.05],
    [50, 1],
    [100, 1.05],
    [250, 1.1],
    [500, 1.15],
    [1000, 1.2],
    [2500, 1.25],
    [5000, 1.25]
  ];

  function roundCurrency(value) {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  function isValidQuantity(config, quantity) {
    var numeric = Number(quantity);
    var rules = config && config.quantity;
    if (!rules || !Number.isInteger(numeric) ||
        numeric < Number(rules.minimum) ||
        numeric > Number(rules.maximum)) return false;
    if ((rules.special_quantities || []).map(Number).indexOf(numeric) !== -1) return true;
    return numeric >= Number(rules.multiple_start) &&
      numeric % Number(rules.multiple) === 0;
  }

  function adjustmentBounds(anchors, quantity) {
    for (var index = 0; index < anchors.length; index += 1) {
      if (Number(anchors[index].quantity) === quantity) {
        return { low: anchors[index], high: anchors[index] };
      }
      if (Number(anchors[index].quantity) > quantity) {
        return { low: anchors[index - 1], high: anchors[index] };
      }
    }
    return null;
  }

  function commercialMultiplier(config, quantity) {
    var anchors = config.commercial_adjustment_anchors;
    var bounds = adjustmentBounds(anchors, Number(quantity));
    if (!bounds) return null;
    var lowQuantity = Number(bounds.low.quantity);
    var highQuantity = Number(bounds.high.quantity);
    var lowMultiplier = Number(bounds.low.multiplier);
    var highMultiplier = Number(bounds.high.multiplier);
    if (lowQuantity === highQuantity) return lowMultiplier;
    var progress = (Number(quantity) - lowQuantity) / (highQuantity - lowQuantity);
    return Math.round(
      (lowMultiplier + (highMultiplier - lowMultiplier) * progress) * 1000000000000
    ) / 1000000000000;
  }

  function validate(config, foundationEngine) {
    var errors = [];
    if (!config || config.schema_version !== 1) errors.push('schema version 1');
    if (!config || config.family !== 'label_sheets') errors.push('label_sheets family');
    if (!config || config.status !== 'approved_launch_v1') errors.push('approved launch status');
    if (!config || config.currency !== 'USD') errors.push('USD currency');
    if (!config || config.mode !== 'roll_labels_commercial_curve_v1') {
      errors.push('Roll Labels commercial curve mode');
    }
    var foundation = config && config.foundation;
    if (!foundation ||
        foundation.family !== 'roll_labels' ||
        foundation.asset !== 'zug-roll-labels-pricing.json' ||
        foundation.schema_version !== 2 ||
        foundation.mode !== 'market_calibrated_parametric_v1' ||
        Number(foundation.minimum_foundation_quantity) !== 50) {
      errors.push('approved Roll Labels foundation');
    }
    var quantity = config && config.quantity;
    if (!quantity ||
        Number(quantity.minimum) !== 25 ||
        Number(quantity.maximum) !== 5000 ||
        Number(quantity.multiple_start) !== 30 ||
        Number(quantity.multiple) !== 10 ||
        !Array.isArray(quantity.special_quantities) ||
        quantity.special_quantities.length !== 1 ||
        Number(quantity.special_quantities[0]) !== 25) {
      errors.push('approved Label Sheets quantity rules');
    }
    var anchors = config && config.commercial_adjustment_anchors;
    if (!Array.isArray(anchors) ||
        anchors.length !== EXPECTED_ANCHORS.length ||
        anchors.some(function(anchor, index) {
          return Number(anchor.quantity) !== EXPECTED_ANCHORS[index][0] ||
            Number(anchor.multiplier) !== EXPECTED_ANCHORS[index][1];
        })) {
      errors.push('approved commercial adjustment curve');
    }
    var transition = config && config.introductory_transition;
    if (!transition ||
        Number(transition.from_quantity) !== 50 ||
        Number(transition.to_quantity) !== 100 ||
        transition.method !== 'linear_order_total') {
      errors.push('approved introductory order-total transition');
    }
    if (!foundationEngine ||
        !foundationEngine.validation ||
        foundationEngine.validation.valid !== true ||
        typeof foundationEngine.resolve !== 'function') {
      errors.push('valid Roll Labels pricing engine');
    }
    return { valid: errors.length === 0, errors: errors };
  }

  function create(config, foundationEngine) {
    var validation = validate(config, foundationEngine);

    function resolve(request) {
      if (!validation.valid || !request || !isValidQuantity(config, request.quantity)) return null;
      var quantity = Number(request.quantity);
      var foundationQuantity = Math.max(
        quantity,
        Number(config.foundation.minimum_foundation_quantity)
      );
      var foundationResult = foundationEngine.resolve({
        width: request.width,
        height: request.height,
        quantity: foundationQuantity,
        material: request.material,
        finish: request.finish,
        turnaround: 'regular'
      });
      var rushFoundation = foundationEngine.resolve({
        width: request.width,
        height: request.height,
        quantity: foundationQuantity,
        material: request.material,
        finish: request.finish,
        turnaround: request.turnaround || 'regular'
      });
      var multiplier = commercialMultiplier(config, quantity);
      if (!foundationResult || !rushFoundation || !(multiplier > 0)) return null;

      var regularFoundationUnit = foundationResult.regular_total / foundationQuantity;
      var regularTotal = roundCurrency(regularFoundationUnit * quantity * multiplier);
      var transition = config.introductory_transition;
      if (quantity > Number(transition.from_quantity) &&
          quantity < Number(transition.to_quantity)) {
        var transitionLowQuantity = Number(transition.from_quantity);
        var transitionHighQuantity = Number(transition.to_quantity);
        var lowFoundation = foundationEngine.resolve({
          width: request.width,
          height: request.height,
          quantity: transitionLowQuantity,
          material: request.material,
          finish: request.finish,
          turnaround: 'regular'
        });
        var highFoundation = foundationEngine.resolve({
          width: request.width,
          height: request.height,
          quantity: transitionHighQuantity,
          material: request.material,
          finish: request.finish,
          turnaround: 'regular'
        });
        if (!lowFoundation || !highFoundation) return null;
        var lowTotal = lowFoundation.regular_total *
          commercialMultiplier(config, transitionLowQuantity);
        var highTotal = highFoundation.regular_total *
          commercialMultiplier(config, transitionHighQuantity);
        var transitionProgress = (quantity - transitionLowQuantity) /
          (transitionHighQuantity - transitionLowQuantity);
        regularTotal = roundCurrency(lowTotal + (highTotal - lowTotal) * transitionProgress);
      }
      var rushMultiplier = Number(rushFoundation.rush_mult);
      var total = roundCurrency(regularTotal * rushMultiplier);
      var curveBounds = adjustmentBounds(config.commercial_adjustment_anchors, quantity);
      var effectiveMultiplier = regularTotal / (regularFoundationUnit * quantity);
      return {
        version: 'label_sheets_commercial_v1',
        pricing_table_version: config.pricing_table_version + '+' + foundationResult.pricing_table_version,
        width_in: foundationResult.width_in,
        height_in: foundationResult.height_in,
        area_in2: foundationResult.area_in2,
        size_key: foundationResult.size_key,
        size_tier_in2: foundationResult.size_tier_in2,
        qty_entered: quantity,
        q_low: foundationResult.q_low,
        q_high: foundationResult.q_high,
        u_interp: regularTotal / quantity,
        u_final: total / quantity,
        global_base_scalar: 1,
        regular_total: regularTotal,
        total_desired: total,
        unit_price: total / quantity,
        material_key: request.material,
        material_mult: 1,
        finish_key: request.finish,
        rush_option: request.turnaround || 'regular',
        rush_mult: rushMultiplier,
        quantity: quantity,
        cart_mode: 'product_single',
        order_adjustment_cents: 0,
        foundation_family: 'roll_labels',
        foundation_quantity: foundationQuantity,
        commercial_multiplier: multiplier,
        effective_commercial_multiplier: effectiveMultiplier,
        commercial_q_low: Number(curveBounds.low.quantity),
        commercial_q_high: Number(curveBounds.high.quantity)
      };
    }

    return {
      validation: validation,
      isValidQuantity: function(quantity) {
        return validation.valid && isValidQuantity(config, quantity);
      },
      commercialMultiplier: function(quantity) {
        return validation.valid ? commercialMultiplier(config, Number(quantity)) : null;
      },
      resolve: resolve
    };
  }

  root.ZUG_LABEL_SHEETS_PRICING_ENGINE = {
    create: create,
    validate: validate,
    isValidQuantity: isValidQuantity
  };
})(typeof window !== 'undefined' ? window : this);
